// Features: M004-F0315, M004-F0318, M004-F0322, M004-F0323, M005-F0013, M005-F0017, M005-F0022
// Tests: T-M004-0004, T-M004-0009, T-M004-0010, WEB-TERM-027, WEB-TERM-028, WEB-TERM-029,
// WEB-TERM-030, WEB-TERM-031, WEB-TERM-033
// Term slot sentence wording (M005-F0013, design §10.5b): src/utils/termSlotWording.js

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../layout/Layout';
import { classProgrammeApi, organisationApi, scheduleApi, termApi } from '../../services/api';
import useDismissOnOutsideClick from '../../hooks/useDismissOnOutsideClick';
import {
  formatRoomPhrase,
  formatTermRangeSentence,
  formatWeekdayTimeSentence,
  getTermSlotStatusWording,
} from '../../utils/termSlotWording';
import './Teacher.css';

const DEFAULT_TIMEZONE = 'Europe/London';
const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TEACHER_WEEK_DAYS = [1, 2, 3, 4, 5, 6, 0];

const createDefaultForm = () => {
  const start = new Date();
  start.setDate(start.getDate() + 1);
  start.setMinutes(0, 0, 0);
  const end = new Date(start);
  end.setHours(end.getHours() + 1);

  return {
    group_id: '',
    title: '',
    description: '',
    start_time: toDateTimeInput(start),
    end_time: toDateTimeInput(end),
    recurrence_rule: '',
    recurrence_timezone: DEFAULT_TIMEZONE,
    recurrence_end_time: '',
    status: 'scheduled',
    blob: '{}',
    class_programme_id: '',
    public_title_override: '',
    public_description_override: '',
    public_visible: false,
  };
};

const unwrapData = (response) => response?.data?.data ?? response?.data ?? response;

const unwrapOrganisationList = (response) => {
  const payload = unwrapData(response);
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.organisations)) return payload.organisations;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getOrganisationId = (organisation) => Number(organisation?.id || organisation?.organisation_id || 0);

const unwrapClassList = (response) => {
  const payload = unwrapData(response);
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.classes)) {
    return payload.classes;
  }
  return [];
};

const unwrapTermBookingList = (response) => {
  const payload = unwrapData(response);
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.bookings)) {
    return payload.bookings;
  }
  if (Array.isArray(payload?.items)) {
    return payload.items;
  }
  return [];
};

function toDateTimeInput(value) {
  if (!value) {
    return '';
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (part) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const toIsoDateTime = (value) => (value ? new Date(value).toISOString() : undefined);

const formatDate = (value) => {
  if (!value) {
    return 'No date set';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Invalid date';
  }

  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const formatDateTime = (value) => {
  if (!value) {
    return 'No date set';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Invalid date';
  }

  return date.toLocaleString('en-GB', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateOnly = (value) => {
  if (!value) {
    return 'Date to be confirmed';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Date to be confirmed';
  }

  return date.toLocaleDateString('en-GB', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTermTime = (value) => (value ? String(value).slice(0, 5) : 'TBC');

const formatTermDateRange = (booking) => {
  const start = booking.start_date || booking.term_start_date || booking.coverage_start_date;
  const end = booking.end_date || booking.term_end_date || booking.coverage_end_date;
  return `${formatDateOnly(start)} - ${formatDateOnly(end)}`;
};

const getTermBookingStatus = (booking) => booking.status || booking.invitation_status || 'pending';

const getTermBookingId = (booking) => booking.id || booking.booking_id || booking.term_booking_id || booking.invitation_id;

const getTermInvitationId = (booking) => booking.invitation_id || getTermBookingId(booking);

const getTermBookingTitle = (booking) => booking.title || booking.term_name || booking.slot_label || booking.label || 'Term teaching booking';

const getTermInvitationMessage = (booking) => {
  if (booking.expiry_message) {
    return booking.expiry_message;
  }
  if (booking.status === 'cancelled' || booking.invitation_status === 'cancelled') {
    return 'This invitation was cancelled by an admin and can no longer be accepted or rejected.';
  }
  if (booking.status === 'expired' || booking.invitation_status === 'expired') {
    return 'This invitation has expired and can no longer be accepted or rejected.';
  }
  if (booking.term_has_started && booking.status === 'rejected') {
    return 'This term has already started, so further rejection changes are blocked.';
  }
  return '';
};

const canActOnTermInvitation = (booking) => {
  const status = getTermBookingStatus(booking);
  return !['accepted', 'admin_confirmed', 'rejected', 'cancelled', 'expired'].includes(status);
};

const isCurrentOrFutureTermBooking = (booking) => {
  const end = booking.end_date || booking.term_end_date || booking.coverage_end_date;
  if (!end) {
    return true;
  }
  const endDate = new Date(end);
  if (Number.isNaN(endDate.getTime())) {
    return true;
  }
  endDate.setHours(23, 59, 59, 999);
  return endDate >= new Date();
};

const groupTermBookingsByWeekday = (bookings) => {
  const grouped = TEACHER_WEEK_DAYS.reduce((days, weekday) => ({
    ...days,
    [weekday]: [],
  }), {});

  bookings.forEach((booking) => {
    const weekday = Number.isInteger(Number(booking.weekday)) ? Number(booking.weekday) : 0;
    grouped[weekday] = [...(grouped[weekday] || []), booking];
  });

  Object.keys(grouped).forEach((weekday) => {
    grouped[weekday].sort((first, second) => (
      String(first.start_time || '').localeCompare(String(second.start_time || ''))
    ));
  });

  return grouped;
};

const TermBookingDetailDrawer = ({
  booking,
  actionId,
  onClose,
  onAccept,
  onReject,
  onUpdateRenewal,
}) => {
  // M004-F0612 / T-M004-0019: dismiss only on a genuine outside press-and-release;
  // a text-selection drag from inside a dialog to outside it must not close it.
  // Declared unconditionally (rules of hooks) ahead of the `!booking` early return below.
  const drawerDismissHandlers = useDismissOnOutsideClick(onClose);

  if (!booking) {
    return null;
  }

  const bookingId = getTermBookingId(booking);
  const invitationId = getTermInvitationId(booking);
  const isBusy = actionId === bookingId || actionId === invitationId;
  const canAutoRenew = booking.auto_renew_admin !== false;
  const canAutoAccept = booking.auto_accept_admin !== false;
  const canAct = canActOnTermInvitation(booking);
  const invitationMessage = getTermInvitationMessage(booking);
  const status = getTermBookingStatus(booking);
  const statusWording = getTermSlotStatusWording(status);

  return (
    <div className="term-booking-drawer-backdrop" role="presentation" {...drawerDismissHandlers}>
      <aside
        className="term-booking-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="term-booking-drawer-title"
        data-testid="teacher-term-booking-drawer"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="term-booking-drawer-header">
          <div>
            <h2 id="term-booking-drawer-title">{getTermBookingTitle(booking)}</h2>
            <p>{booking.room_name || 'Room to be confirmed'}</p>
          </div>
          <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
        </div>

        {invitationMessage && <div className="class-alert error">{invitationMessage}</div>}

        <dl className="term-booking-detail-list">
          <div>
            <dt>Status</dt>
            <dd>
              <span className={`class-status ${status}`}>{statusWording.label}</span>
              {statusWording.meaning && (
                <span className="term-slot-status-meaning"> — {statusWording.meaning}</span>
              )}
            </dd>
          </div>
          <div>
            <dt>Term dates</dt>
            <dd>{formatTermDateRange(booking)}</dd>
          </div>
          <div>
            <dt>Weekly slot</dt>
            <dd>{WEEKDAY_LABELS[Number(booking.weekday) || 0]} {formatTermTime(booking.start_time)}-{formatTermTime(booking.end_time)}</dd>
          </div>
          <div>
            <dt>Teacher</dt>
            <dd>{booking.teacher_name || booking.teacher_email || booking.user_name || 'Assigned to you'}</dd>
          </div>
          {booking.source_booking_id && (
            <div>
              <dt>Renewed from</dt>
              <dd>Booking #{booking.source_booking_id}</dd>
            </div>
          )}
          {booking.sync_status && (
            <div>
              <dt>Calendar sync</dt>
              <dd>{booking.sync_status}</dd>
            </div>
          )}
          {booking.notification_status && (
            <div>
              <dt>Notification</dt>
              <dd>{booking.notification_status}</dd>
            </div>
          )}
        </dl>

        {(canAutoRenew || canAutoAccept) && (
          <section className="term-booking-drawer-section" aria-label="Teacher preferences">
            {canAutoRenew && (
              <label className="term-checkbox-row compact">
                <input
                  data-testid="teacher-term-auto-renew-checkbox"
                  type="checkbox"
                  checked={booking.auto_renew_user !== false}
                  disabled={isBusy}
                  onChange={(event) => onUpdateRenewal(booking, {
                    auto_renew_user: event.target.checked,
                    auto_accept_user: booking.auto_accept_user !== false,
                  })}
                />
                Renew booking. This will request to rebook this slot for the next term.
              </label>
            )}
            {canAutoAccept && (
              <label className="term-checkbox-row compact">
                <input
                  data-testid="teacher-term-auto-accept-checkbox"
                  type="checkbox"
                  checked={Boolean(booking.auto_accept_user)}
                  disabled={isBusy}
                  onChange={(event) => onUpdateRenewal(booking, {
                    auto_renew_user: booking.auto_renew_user !== false,
                    auto_accept_user: event.target.checked,
                  })}
                />
                Auto-accept renewal. Keep this enabled so the booking does not need to be accepted each term.
              </label>
            )}
          </section>
        )}

        <div className="term-booking-drawer-actions" data-testid="teacher-term-booking-drawer-actions">
          <button type="button" className="btn btn-success" disabled={isBusy || !canAct} onClick={() => onAccept(booking)}>
            Accept
          </button>
          <button type="button" className="btn btn-danger" disabled={isBusy || !canAct || booking.term_has_started} onClick={() => onReject(booking)}>
            Reject
          </button>
        </div>
      </aside>
    </div>
  );
};

const summarizeBlob = (blob) => {
  if (blob === null || blob === undefined || blob === '') {
    return 'No blob';
  }

  const text = typeof blob === 'string' ? blob : JSON.stringify(blob);
  return text.length > 96 ? `${text.slice(0, 96)}...` : text;
};

const parseBlob = (value) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  return JSON.parse(trimmed);
};

export const classToForm = (classItem) => ({
  group_id: classItem?.group_id ? String(classItem.group_id) : '',
  title: classItem?.title || '',
  description: classItem?.description || '',
  start_time: toDateTimeInput(classItem?.start_time),
  end_time: toDateTimeInput(classItem?.end_time),
  recurrence_rule: classItem?.recurrence_rule || '',
  recurrence_timezone: classItem?.recurrence_timezone || DEFAULT_TIMEZONE,
  recurrence_end_time: toDateTimeInput(classItem?.recurrence_end_time),
  status: classItem?.status || 'scheduled',
  blob: classItem?.blob === null || classItem?.blob === undefined
    ? ''
    : JSON.stringify(classItem.blob, null, 2),
  class_programme_id: classItem?.class_programme_id || '',
  public_title_override: classItem?.public_title_override || '',
  public_description_override: classItem?.public_description_override || '',
  public_visible: Boolean(classItem?.public_visible),
});

export const buildPayload = (form, { includeStatus = false, includeGroupId = true } = {}) => {
  const payload = {
    title: form.title.trim(),
    description: form.description.trim() || null,
    blob: parseBlob(form.blob),
    start_time: toIsoDateTime(form.start_time),
    end_time: toIsoDateTime(form.end_time),
    recurrence_rule: form.recurrence_rule.trim() || null,
    recurrence_timezone: form.recurrence_timezone.trim() || DEFAULT_TIMEZONE,
    recurrence_end_time: toIsoDateTime(form.recurrence_end_time) || null,
    class_programme_id: form.class_programme_id || null,
    public_title_override: form.public_title_override.trim() || null,
    public_description_override: form.public_description_override.trim() || null,
    public_visible: Boolean(form.public_visible),
  };

  if (includeGroupId) {
    payload.group_id = Number(form.group_id);
  }

  if (includeStatus) {
    payload.status = form.status;
  }

  return payload;
};

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message
  || error?.response?.data?.error?.message
  || error?.message
  || fallback
);

// A definitive Core denial for the Website programmes list: 403 with the
// CLASS_PROGRAMME_ACCESS_DENIED code. Any other status/code (429, 5xx,
// network) is treated as transient and must not hide the tab — see #56 /
// pipeline 8077 WEB-TERM-033.
const isProgrammeAccessDenied = (error) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.error?.code || error?.response?.data?.code;
  return status === 403 && code === 'CLASS_PROGRAMME_ACCESS_DENIED';
};

const ClassForm = ({
  form,
  setForm,
  onSubmit,
  onCancel,
  submitting,
  submitLabel,
  showStatus = false,
  allowGroupChange = true,
  programmes = [],
}) => {
  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  return (
    <form className="class-editor" onSubmit={onSubmit}>
      <div className="form-grid">
        <label>
          Group ID
          <input
            data-testid="class-group-id-input"
            type="number"
            min="1"
            value={form.group_id}
            onChange={(event) => updateField('group_id', event.target.value)}
            disabled={!allowGroupChange}
            required
          />
        </label>
        <label>
          Title
          <input
            data-testid="class-title-input"
            type="text"
            maxLength="255"
            value={form.title}
            onChange={(event) => updateField('title', event.target.value)}
            required
          />
        </label>
        <label>
          Start
          <input
            data-testid="class-start-input"
            type="datetime-local"
            value={form.start_time}
            onChange={(event) => updateField('start_time', event.target.value)}
            required
          />
        </label>
        <label>
          End
          <input
            data-testid="class-end-input"
            type="datetime-local"
            value={form.end_time}
            onChange={(event) => updateField('end_time', event.target.value)}
            required
          />
        </label>
        <label>
          Repeat
          <input
            data-testid="class-recurrence-rule-input"
            type="text"
            placeholder="FREQ=WEEKLY;BYDAY=MO"
            value={form.recurrence_rule}
            onChange={(event) => updateField('recurrence_rule', event.target.value)}
          />
        </label>
        <label>
          Timezone
          <input
            data-testid="class-timezone-input"
            type="text"
            value={form.recurrence_timezone}
            onChange={(event) => updateField('recurrence_timezone', event.target.value)}
            required
          />
        </label>
        <label>
          Repeat ends
          <input
            data-testid="class-recurrence-end-input"
            type="datetime-local"
            value={form.recurrence_end_time}
            onChange={(event) => updateField('recurrence_end_time', event.target.value)}
          />
        </label>
        {showStatus && (
          <label>
            Status
            <select
              data-testid="class-status-select"
              value={form.status}
              onChange={(event) => updateField('status', event.target.value)}
            >
              <option value="scheduled">Scheduled</option>
              <option value="cancelled">Cancelled</option>
              <option value="archived">Archived</option>
            </select>
          </label>
        )}
      </div>

      <label>
        Description
        <textarea
          data-testid="class-description-input"
          value={form.description}
          onChange={(event) => updateField('description', event.target.value)}
          rows="3"
        />
      </label>

      <fieldset className="public-programme-fields">
        <legend>Website programme</legend>
        <label>
          Reusable programme
          <select data-testid="class-programme-select" value={form.class_programme_id} onChange={(event) => updateField('class_programme_id', event.target.value)}>
            <option value="">Not linked</option>
            {programmes.filter((programme) => programme.status !== 'archived').map((programme) => (
              <option key={programme.id} value={programme.id}>{programme.public_title || programme.title || programme.slug}</option>
            ))}
          </select>
        </label>
        <label className="term-checkbox-row compact">
          <input data-testid="class-public-visible" type="checkbox" checked={form.public_visible} onChange={(event) => updateField('public_visible', event.target.checked)} />
          Show this scheduled run on the public website
        </label>
        <label>
          Public title override
          <input data-testid="class-public-title-override" value={form.public_title_override} onChange={(event) => updateField('public_title_override', event.target.value)} placeholder="Use the programme title" />
        </label>
        <label>
          Public description override
          <textarea data-testid="class-public-description-override" value={form.public_description_override} onChange={(event) => updateField('public_description_override', event.target.value)} rows="3" placeholder="Use the programme description" />
        </label>
      </fieldset>

      <label>
        Blob JSON
        <textarea
          data-testid="class-blob-input"
          className="blob-editor"
          value={form.blob}
          onChange={(event) => updateField('blob', event.target.value)}
          rows="7"
        />
      </label>

      <div className="editor-actions">
        <button className="btn btn-primary" type="submit" disabled={submitting}>
          {submitting ? 'Saving...' : submitLabel}
        </button>
        <button className="btn btn-outline" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
};

// ==================== Teacher Classes ====================
const TeacherSchedule = () => {
  const [activeSection, setActiveSection] = useState('classes');
  const [classes, setClasses] = useState([]);
  const [termBookings, setTermBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [termLoading, setTermLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [termActionId, setTermActionId] = useState('');
  const [selectedTermBooking, setSelectedTermBooking] = useState(null);
  const [error, setError] = useState('');
  const [termError, setTermError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showForm, setShowForm] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [form, setForm] = useState(createDefaultForm);
  const [programmes, setProgrammes] = useState([]);
  // Scoped to the "Website programmes" tab: a programmes fetch failure (eg. the
  // Core 403 CLASS_PROGRAMME_ACCESS_DENIED members with no managed group get)
  // must never surface as the page-level `error`/class-error banner. See #56.
  const [programmesError, setProgrammesError] = useState('');
  // Denial-driven, not success-driven (see #56 / pipeline 8077 WEB-TERM-033):
  // only a definitive Core 403 CLASS_PROGRAMME_ACCESS_DENIED for the selected
  // organisation sets this true. Any other failure (429, 5xx, network) must
  // leave the tab visible with the inline `programme-error` notice — a
  // transient failure must never permanently hide the manager. Reset to
  // false whenever a list call succeeds, or when the organisation changes
  // (fetchProgrammes runs again and resets it before deciding).
  const [programmesDenied, setProgrammesDenied] = useState(false);
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState('');
  const [organisationsLoading, setOrganisationsLoading] = useState(true);
  const [programmeForm, setProgrammeForm] = useState({ slug: '', public_title: '', short_description: '', full_description: '', audience: '', level: '', booking_url: '', status: 'draft' });
  const [editingProgrammeId, setEditingProgrammeId] = useState('');
  // Visible by default; hidden only on a definitive denial for the selected
  // organisation. Any other fetch failure keeps it, backed by the inline
  // `programme-error` notice. See #56 / pipeline 8077 WEB-TERM-033.
  const programmesTabVisible = programmes.length > 0 || !programmesDenied;

  const fetchProgrammes = useCallback(async () => {
    const organisationId = Number(selectedOrganisationId);
    if (!Number.isInteger(organisationId) || organisationId <= 0) {
      setProgrammes([]);
      setProgrammesDenied(false);
      return;
    }
    try {
      const payload = unwrapData(await classProgrammeApi.list({ org: organisationId, include_archived: true }));
      setProgrammes(Array.isArray(payload) ? payload : (payload?.programmes || payload?.items || []));
      setProgrammesError('');
      setProgrammesDenied(false);
    } catch (fetchError) {
      setProgrammesError(getErrorMessage(fetchError, 'Unable to load class programmes.'));
      setProgrammesDenied(isProgrammeAccessDenied(fetchError));
    }
  }, [selectedOrganisationId]);

  useEffect(() => {
    let current = true;
    organisationApi.getMine().then((response) => {
      if (!current) return;
      const accessible = unwrapOrganisationList(response).filter((organisation) => getOrganisationId(organisation) > 0);
      const primary = accessible.find((organisation) => organisation.is_primary) || accessible[0];
      setOrganisations(accessible);
      setSelectedOrganisationId(primary ? String(getOrganisationId(primary)) : '');
      setOrganisationsLoading(false);
    }).catch((organisationError) => {
      if (!current) return;
      setOrganisations([]);
      setSelectedOrganisationId('');
      setOrganisationsLoading(false);
      setError(getErrorMessage(organisationError, 'Unable to load accessible organisations.'));
    });
    return () => { current = false; };
  }, []);

  const fetchClasses = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await scheduleApi.getClasses({ limit: 100 });
      setClasses(unwrapClassList(response));
    } catch (fetchError) {
      setError(getErrorMessage(fetchError, 'Unable to load classes.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTermBookings = useCallback(async () => {
    setTermLoading(true);
    setTermError('');
    try {
      const response = await termApi.listMyTermBookings();
      setTermBookings(unwrapTermBookingList(response).filter(isCurrentOrFutureTermBooking));
    } catch (fetchError) {
      setTermError(getErrorMessage(fetchError, 'Unable to load term teaching bookings.'));
    } finally {
      setTermLoading(false);
    }
  }, []);

  const refreshSchedule = useCallback(async () => {
    await Promise.all([fetchClasses(), fetchTermBookings(), fetchProgrammes()]);
  }, [fetchClasses, fetchTermBookings, fetchProgrammes]);

  useEffect(() => {
    refreshSchedule();
  }, [refreshSchedule]);

  useEffect(() => {
    if (!programmesTabVisible && activeSection === 'programmes') {
      setActiveSection('classes');
    }
  }, [programmesTabVisible, activeSection]);

  const filteredClasses = useMemo(() => {
    const query = search.trim().toLowerCase();

    return classes.filter((classItem) => {
      const status = classItem.status || 'scheduled';
      const statusMatches = statusFilter === 'all'
        || (statusFilter === 'active' && status === 'scheduled')
        || status === statusFilter;
      const queryMatches = !query
        || classItem.title?.toLowerCase().includes(query)
        || classItem.group_name?.toLowerCase().includes(query)
        || String(classItem.group_id || '').includes(query);

      return statusMatches && queryMatches;
    });
  }, [classes, search, statusFilter]);

  const termBookingsByWeekday = useMemo(() => (
    groupTermBookingsByWeekday(termBookings)
  ), [termBookings]);

  const actionableTermBookings = useMemo(() => (
    termBookings.filter(canActOnTermInvitation)
  ), [termBookings]);

  const runTermAction = async (booking, action, successMessage, localPatch = {}) => {
    const bookingId = getTermBookingId(booking);
    const invitationId = getTermInvitationId(booking);
    setTermActionId(bookingId || invitationId);
    setTermError('');
    setNotice('');

    try {
      await action();
      setNotice(successMessage);
      await fetchTermBookings();
      setSelectedTermBooking((current) => (
        current && getTermBookingId(current) === bookingId
          ? { ...current, ...localPatch }
          : current
      ));
    } catch (termActionError) {
      setTermError(getErrorMessage(termActionError, 'Unable to update term teaching booking.'));
    } finally {
      setTermActionId('');
    }
  };

  const handleTermRenewalChange = (booking, patch) => runTermAction(
    booking,
    () => termApi.updateMyRenewal(getTermBookingId(booking), patch),
    'Term booking preference saved.',
    patch,
  );

  const handleTermAccept = (booking) => runTermAction(
    booking,
    () => termApi.acceptInvitation(getTermInvitationId(booking)),
    'Term booking accepted.',
    { status: 'accepted', invitation_status: 'accepted' },
  );

  const handleTermReject = (booking) => {
    const reason = window.prompt('Reason for rejecting this term teaching booking?') || '';
    return runTermAction(
      booking,
      () => termApi.rejectInvitation(getTermInvitationId(booking), { reason }),
      'Term booking rejected.',
      { status: 'rejected', invitation_status: 'rejected' },
    );
  };

  const openTermBookingDrawer = (booking) => {
    setSelectedTermBooking(booking);
  };

  const startCreate = () => {
    setEditingClass(null);
    setForm(createDefaultForm());
    setShowForm(true);
    setError('');
    setNotice('');
  };

  const startEdit = (classItem) => {
    setEditingClass(classItem);
    setForm(classToForm(classItem));
    setShowForm(true);
    setError('');
    setNotice('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');

    try {
      const payload = buildPayload(form, {
        includeStatus: Boolean(editingClass),
        includeGroupId: !editingClass,
      });
      if (!editingClass && (!Number.isInteger(payload.group_id) || payload.group_id <= 0)) {
        throw new Error('Group ID must be a positive number.');
      }
      if (new Date(payload.end_time).getTime() <= new Date(payload.start_time).getTime()) {
        throw new Error('End time must be after start time.');
      }

      if (editingClass) {
        await scheduleApi.updateClass(editingClass.id, payload);
        setNotice('Class updated.');
      } else {
        await scheduleApi.createClass(payload);
        setNotice('Class created.');
      }

      setShowForm(false);
      setEditingClass(null);
      await fetchClasses();
    } catch (submitError) {
      setError(getErrorMessage(submitError, 'Unable to save class.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (classItem) => {
    const confirmed = window.confirm(`Delete "${classItem.title}"?`);
    if (!confirmed) {
      return;
    }

    setDeletingId(classItem.id);
    setError('');
    setNotice('');
    try {
      await scheduleApi.deleteClass(classItem.id);
      setNotice('Class deleted.');
      await fetchClasses();
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, 'Unable to delete class.'));
    } finally {
      setDeletingId(null);
    }
  };

  const saveProgramme = async (event) => {
    event.preventDefault();
    setError('');
    try {
      const organisationId = Number(selectedOrganisationId);
      if (!Number.isInteger(organisationId) || organisationId <= 0) {
        throw new Error('Choose an accessible organisation before saving a website programme.');
      }
      const { status, ...copyFields } = programmeForm;
      const payload = Object.fromEntries(Object.entries(copyFields).map(([key, value]) => [key, typeof value === 'string' ? (value.trim() || null) : value]));
      if (editingProgrammeId) {
        const previous = programmes.find((programme) => String(programme.id) === String(editingProgrammeId));
        await classProgrammeApi.update(editingProgrammeId, payload);
        if (status !== previous?.status) await classProgrammeApi.updateStatus(editingProgrammeId, status);
      } else {
        const created = unwrapData(await classProgrammeApi.create({ ...payload, organisation_id: organisationId }));
        if (status === 'published' && created?.id) await classProgrammeApi.updateStatus(created.id, status);
      }
      setProgrammeForm({ slug: '', public_title: '', short_description: '', full_description: '', audience: '', level: '', booking_url: '', status: 'draft' });
      setEditingProgrammeId('');
      setNotice('Class programme saved.');
      await fetchProgrammes();
    } catch (programmeError) {
      setError(getErrorMessage(programmeError, 'Unable to save class programme.'));
    }
  };

  const editProgramme = (programme) => {
    setEditingProgrammeId(programme.id);
    setProgrammeForm({
      slug: programme.slug || '', public_title: programme.public_title || programme.title || '',
      short_description: programme.short_description || '', full_description: programme.full_description || '',
      audience: programme.audience || '', level: programme.level || '', booking_url: programme.booking_url || '', status: programme.status || 'draft',
    });
  };

  const archiveProgramme = async (programme) => {
    if (!window.confirm(`Archive "${programme.public_title || programme.title}"?`)) return;
    await classProgrammeApi.archive(programme.id);
    setNotice('Class programme archived.');
    await fetchProgrammes();
  };

  return (
    <Layout>
      <div className="teacher-schedule">
        <header className="page-header">
          <div>
            <h1>Classes</h1>
            <p className="subtitle">{activeSection === 'classes' ? `${filteredClasses.length} of ${classes.length} classes shown · ${termBookings.length} term bookings` : 'Reusable public class content for the selected organisation'}</p>
          </div>
          {activeSection === 'classes' && <div className="header-actions-inline">
            <button className="btn btn-outline" onClick={refreshSchedule} disabled={loading || termLoading}>
              Refresh
            </button>
            <button className="btn btn-primary" onClick={startCreate}>
              New class
            </button>
          </div>}
        </header>

        <nav className="classes-workspace-tabs" role="tablist" aria-label="Classes workspace" data-testid="classes-workspace-tabs">
          <button type="button" role="tab" id="classes-tab" aria-controls="classes-panel" aria-selected={activeSection === 'classes'} className={activeSection === 'classes' ? 'active' : ''} onClick={() => setActiveSection('classes')}>Classes</button>
          {programmesTabVisible && <button type="button" role="tab" id="website-programmes-tab" aria-controls="website-programmes-panel" aria-selected={activeSection === 'programmes'} className={activeSection === 'programmes' ? 'active' : ''} data-testid="website-programmes-tab" onClick={() => setActiveSection('programmes')}>Website programmes</button>}
          <Link role="tab" aria-selected="false" className="classes-workspace-link" data-testid="term-planner-tab" to="/app/teacher/term-planner">Term Planner</Link>
        </nav>

        {activeSection === 'classes' && <div className="class-toolbar">
          <input
            data-testid="class-search-input"
            type="search"
            placeholder="Search title, group, or ID"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="view-toggle" aria-label="Class status filter">
            {['active', 'cancelled', 'archived', 'all'].map((status) => (
              <button
                key={status}
                className={`toggle-btn ${statusFilter === status ? 'active' : ''}`}
                onClick={() => setStatusFilter(status)}
                type="button"
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>}

        {error && <div className="class-alert error" data-testid="class-error">{error}</div>}
        {termError && <div className="class-alert error" data-testid="teacher-term-bookings-error">{termError}</div>}
        {notice && <div className="class-alert success" data-testid="class-notice">{notice}</div>}

        {activeSection === 'programmes' && programmesTabVisible && <section className="programme-manager" id="website-programmes-panel" role="tabpanel" aria-labelledby="website-programmes-tab" data-testid="class-programme-manager">
          <div className="term-classes-header"><div><h2>Website programmes</h2><p>Reusable descriptions that remain attached when weekly classes move between terms.</p></div></div>
          {programmesError && <div className="class-alert error" data-testid="programme-error">{programmesError}</div>}
          <label className="programme-organisation-picker">Organisation
            <select data-testid="programme-organisation-select" value={selectedOrganisationId} disabled={organisationsLoading || organisations.length === 0} onChange={(event) => setSelectedOrganisationId(event.target.value)}>
              {organisations.length === 0 && <option value="">No accessible organisation</option>}
              {organisations.map((organisation) => <option key={getOrganisationId(organisation)} value={getOrganisationId(organisation)}>{organisation.name || organisation.organisation_name || `Organisation ${getOrganisationId(organisation)}`}</option>)}
            </select>
          </label>
          <form className="class-editor" onSubmit={saveProgramme}>
            <div className="form-grid">
              <label>Public title<input required data-testid="programme-title" value={programmeForm.public_title} onChange={(e) => setProgrammeForm({ ...programmeForm, public_title: e.target.value })} /></label>
              <label>Slug<input required data-testid="programme-slug" value={programmeForm.slug} onChange={(e) => setProgrammeForm({ ...programmeForm, slug: e.target.value })} /></label>
              <label>Audience<input value={programmeForm.audience} onChange={(e) => setProgrammeForm({ ...programmeForm, audience: e.target.value })} /></label>
              <label>Level<input value={programmeForm.level} onChange={(e) => setProgrammeForm({ ...programmeForm, level: e.target.value })} /></label>
              <label>Booking URL<input type="url" value={programmeForm.booking_url} onChange={(e) => setProgrammeForm({ ...programmeForm, booking_url: e.target.value })} /></label>
              <label>Status<select data-testid="programme-status" value={programmeForm.status} onChange={(e) => setProgrammeForm({ ...programmeForm, status: e.target.value })}><option value="draft">Draft</option><option value="published">Published</option></select></label>
            </div>
            <label>Short description<textarea required value={programmeForm.short_description} onChange={(e) => setProgrammeForm({ ...programmeForm, short_description: e.target.value })} /></label>
            <label>Full description<textarea value={programmeForm.full_description} onChange={(e) => setProgrammeForm({ ...programmeForm, full_description: e.target.value })} /></label>
            <div className="editor-actions"><button className="btn btn-primary" type="submit" disabled={!selectedOrganisationId}>{editingProgrammeId ? 'Update programme' : 'Create programme'}</button>{editingProgrammeId && <button className="btn btn-outline" type="button" onClick={() => setEditingProgrammeId('')}>Cancel</button>}</div>
          </form>
          <div className="schedule-list">{programmes.map((programme) => <article className="class-card" key={programme.id}><div className="class-info"><h3>{programme.public_title || programme.title}</h3><p>{programme.slug} · {programme.status}</p></div><div className="class-actions"><button className="btn btn-outline" type="button" onClick={() => editProgramme(programme)}>Edit</button>{programme.status !== 'archived' && <button className="btn btn-outline danger" type="button" onClick={() => archiveProgramme(programme)}>Archive</button>}</div></article>)}</div>
        </section>}

        {activeSection === 'classes' && <section id="classes-panel" role="tabpanel" aria-labelledby="classes-tab">
        {!termLoading && actionableTermBookings.length > 0 && (
          <section className="term-action-panel" data-testid="teacher-term-action-required-list">
            <div>
              <h2>Term bookings needing action</h2>
              <p>Review and respond to assigned term teaching slots.</p>
            </div>
            <div className="term-action-list">
              {actionableTermBookings.map((booking) => (
                <article key={getTermBookingId(booking) || getTermInvitationId(booking)} className="term-action-row">
                  <div>
                    <strong>{getTermBookingTitle(booking)}</strong>
                    <span>{booking.room_name || 'Room to be confirmed'} / {WEEKDAY_LABELS[Number(booking.weekday) || 0]} {formatTermTime(booking.start_time)}-{formatTermTime(booking.end_time)}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline"
                    data-testid="teacher-term-action-view-more"
                    onClick={() => openTermBookingDrawer(booking)}
                  >
                    View more info
                  </button>
                </article>
              ))}
            </div>
          </section>
        )}

        <section className="term-classes-panel" data-testid="teacher-term-bookings-section">
          <div className="term-classes-header">
            <div>
              <h2>Your term slots</h2>
              <p>Weekly slots the studio has booked you for this term.</p>
            </div>
          </div>

          {termLoading ? (
            <div className="loading-container compact">
              <div className="spinner"></div>
            </div>
          ) : termBookings.length === 0 ? (
            <div className="empty-state compact">No term teaching bookings yet.</div>
          ) : (
            <div className="teacher-term-slot-list" data-testid="teacher-week-planner">
              {TEACHER_WEEK_DAYS.filter((weekday) => (termBookingsByWeekday[weekday] || []).length > 0).map((weekday) => (
                <section key={weekday} className="teacher-term-weekday-group" aria-label={WEEKDAY_LABELS[weekday]}>
                  {termBookingsByWeekday[weekday].map((booking) => {
                    const statusWording = getTermSlotStatusWording(getTermBookingStatus(booking));
                    return (
                      <button
                        key={getTermBookingId(booking) || getTermInvitationId(booking)}
                        type="button"
                        className="term-slot-sentence"
                        data-testid="teacher-term-booking-card"
                        onClick={() => openTermBookingDrawer(booking)}
                      >
                        <span className="term-slot-sentence-text">
                          <strong>{getTermBookingTitle(booking)}</strong>
                          {' — '}
                          {formatWeekdayTimeSentence(booking.weekday, booking.start_time, booking.end_time)}
                          {' '}
                          {formatRoomPhrase(booking.room_name)}
                          {' · '}
                          {formatTermRangeSentence(booking)}
                        </span>
                        <span className="term-slot-status">
                          <span className={`class-status ${statusWording.key}`}>{statusWording.label}</span>
                          {statusWording.meaning && (
                            <span className="term-slot-status-meaning">{statusWording.meaning}</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </section>
              ))}
            </div>
          )}

          <div className="term-planner-cta">
            <Link className="btn btn-outline" to="/app/teacher/term-planner">
              Open Term Planner
            </Link>
            <p>The studio's weekly term schedule where slots are assigned.</p>
          </div>
        </section>

        {showForm && (
          <section className="class-editor-panel">
            <h2>{editingClass ? 'Edit class' : 'Create class'}</h2>
            <ClassForm
              form={form}
              setForm={setForm}
              onSubmit={handleSubmit}
              onCancel={() => setShowForm(false)}
              submitting={saving}
              submitLabel={editingClass ? 'Update class' : 'Create class'}
              showStatus={Boolean(editingClass)}
              allowGroupChange={!editingClass}
              programmes={programmes}
            />
          </section>
        )}

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="schedule-list">
            {filteredClasses.map((classItem) => (
              <article key={classItem.id} className="class-card" data-testid="class-card">
                <div className="class-date">
                  <span className="date">{formatDate(classItem.start_time)}</span>
                  <span className="time">{formatTime(classItem.start_time)}-{formatTime(classItem.end_time)}</span>
                </div>
                <div className="class-info">
                  <Link to={`/app/teacher/class/${classItem.id}`}>
                    <h3>{classItem.title}</h3>
                  </Link>
                  <p>{classItem.group_name || `Group ${classItem.group_id}`} · {classItem.recurrence_rule || 'One-off class'}</p>
                  <p className="blob-summary">{summarizeBlob(classItem.blob)}</p>
                </div>
                <div className="class-actions">
                  <span className={`class-status ${classItem.status || 'scheduled'}`}>
                    {classItem.status || 'scheduled'}
                  </span>
                  <button className="btn btn-outline" onClick={() => startEdit(classItem)}>
                    Edit
                  </button>
                  <button
                    className="btn btn-outline danger"
                    onClick={() => handleDelete(classItem)}
                    disabled={deletingId === classItem.id}
                  >
                    {deletingId === classItem.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </article>
            ))}
            {!filteredClasses.length && (
              <div className="empty-state">
                <h2>No classes found</h2>
                <p>Create a class or adjust the filters.</p>
              </div>
            )}
          </div>
        )}

        <TermBookingDetailDrawer
          booking={selectedTermBooking}
          actionId={termActionId}
          onClose={() => setSelectedTermBooking(null)}
          onAccept={handleTermAccept}
          onReject={handleTermReject}
          onUpdateRenewal={handleTermRenewalChange}
        />
        </section>}
      </div>
    </Layout>
  );
};

// ==================== Teacher Class Detail ====================
const TeacherClass = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [form, setForm] = useState(createDefaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [programmes, setProgrammes] = useState([]);

  const fetchClass = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await scheduleApi.getClass(id);
      const nextClass = unwrapData(response);
      setClassData(nextClass);
      setForm(classToForm(nextClass));
      try {
        const accessible = unwrapOrganisationList(await organisationApi.getMine()).filter((organisation) => getOrganisationId(organisation) > 0);
        const classOrganisationId = Number(nextClass?.organisation_id || nextClass?.org_id || 0);
        const selectedOrganisation = accessible.find((organisation) => getOrganisationId(organisation) === classOrganisationId)
          || accessible.find((organisation) => organisation.is_primary)
          || accessible[0];
        const organisationId = getOrganisationId(selectedOrganisation);
        if (organisationId > 0) {
          const programmePayload = unwrapData(await classProgrammeApi.list({ org: organisationId }));
          setProgrammes(Array.isArray(programmePayload) ? programmePayload : (programmePayload?.programmes || programmePayload?.items || []));
        } else {
          setProgrammes([]);
        }
      } catch (_programmeError) {
        setProgrammes([]);
      }
    } catch (fetchError) {
      setError(getErrorMessage(fetchError, 'Unable to load class.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchClass();
  }, [fetchClass]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');

    try {
      const payload = buildPayload(form, { includeStatus: true, includeGroupId: false });
      if (new Date(payload.end_time).getTime() <= new Date(payload.start_time).getTime()) {
        throw new Error('End time must be after start time.');
      }
      await scheduleApi.updateClass(id, payload);
      setNotice('Class updated.');
      await fetchClass();
    } catch (submitError) {
      setError(getErrorMessage(submitError, 'Unable to update class.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(`Delete "${classData?.title || 'this class'}"?`);
    if (!confirmed) {
      return;
    }

    setSaving(true);
    setError('');
    try {
      await scheduleApi.deleteClass(id);
      navigate('/app/teacher/classes');
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, 'Unable to delete class.'));
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="teacher-class">
        <header className="page-header">
          <div>
            <Link to="/app/teacher/classes" className="back-link">Back to classes</Link>
            <h1>{classData?.title || 'Class'}</h1>
            <p className="subtitle">
              {classData ? `${formatDateTime(classData.start_time)} to ${formatTime(classData.end_time)}` : 'Loading class'}
            </p>
          </div>
          {classData && (
            <button className="btn btn-outline danger" onClick={handleDelete} disabled={saving}>
              Delete
            </button>
          )}
        </header>

        {error && <div className="class-alert error" data-testid="class-error">{error}</div>}
        {notice && <div className="class-alert success" data-testid="class-notice">{notice}</div>}

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <section className="class-summary-panel">
              <div>
                <span className="summary-label">Group</span>
                <strong>{classData?.group_name || `Group ${classData?.group_id}`}</strong>
              </div>
              <div>
                <span className="summary-label">Repeat</span>
                <strong>{classData?.recurrence_rule || 'One-off'}</strong>
              </div>
              <div>
                <span className="summary-label">Timezone</span>
                <strong>{classData?.recurrence_timezone || DEFAULT_TIMEZONE}</strong>
              </div>
              <div>
                <span className="summary-label">Status</span>
                <strong>{classData?.status || 'scheduled'}</strong>
              </div>
            </section>

            <section className="class-editor-panel">
              <h2>Edit class</h2>
              <ClassForm
                form={form}
                setForm={setForm}
                onSubmit={handleSubmit}
                onCancel={() => navigate('/app/teacher/classes')}
                submitting={saving}
                submitLabel="Update class"
                showStatus
                allowGroupChange={false}
                programmes={programmes}
              />
            </section>
          </>
        )}
      </div>
    </Layout>
  );
};

export { TeacherSchedule, TeacherClass };
